import React from 'react';
import {View} from 'react-native';
import {useRoute} from '@react-navigation/native';
import {isEmpty, isFunction} from 'lodash';

import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {DownlineProfile} from '/constant/TestIDs';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';
import {getFontFamily} from '/util/text.util';

import {styles} from './ProfileSection.style';
import {generateOnPress} from './ProfileSection.util';

interface ProfileSectionProps {
  analyticsTitle: string;
  style: {
    base: {};
    titleText: {};
    title: {};
  };
  title: string;
  onInfoIconPress: () => void;
  children: React.ReactNode;
  actionLabel: string | React.ReactNode;
  onPress: () => void;
  lastPaidRank: string;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  analyticsTitle = '',
  style: styleProp = {},
  title = '',
  onInfoIconPress = null,
  children = null,
  actionLabel = '',
  onPress = null,
  lastPaidRank = '',
}) => {
  const style = [styles.base];
  style.push(styleProp.base);
  const titleStyle = {
    ...styles.titleText,
    ...(styleProp.titleText || {}),
  };
  const textStyle = {...getFontFamily('bold')};

  const {name} = useRoute();

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.informationClick,
  });

  const renderAction = () => {
    if (isEmpty(actionLabel)) {
      return null;
    }

    if (typeof actionLabel === 'string') {
      return (
        <Button
          type="textLink"
          style={{
            // @ts-expect-error TS(2339): Property 'actionButton' does not exist on type '{ ... Remove this comment to see the full error message
            base: styles.actionButton,
            text: textStyle,
          }}
          onPress={onPress}
          {...testProperties(DownlineProfile.profileSectionlink)}>
          {actionLabel}
        </Button>
      );
    }

    return actionLabel;
  };

  return (
    <Gutter style={style}>
      <View style={{...styles.title, ...styleProp?.title}}>
        {title && (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={true}
            accessibilityRole="header"
            variant="heading"
            style={titleStyle}
            {...testProperties(DownlineProfile.profileSectionHeader)}>
            {title}
          </Text>
        )}
        <View style={styles.actionFiller} />
        {renderAction()}

        {isFunction(onInfoIconPress) && (
          <TouchableOpacity
            onPress={generateOnPress({
              analyticsTitle,
              name,
              onInfoIconPress,
              sendEvent,
              lastPaidRank,
            })}
            {...testProperties(null, true)}>
            <Icon name="info" style={styles.infoIcon} />
          </TouchableOpacity>
        )}
      </View>
      {children}
    </Gutter>
  );
};

export default ProfileSection;
